import React from "react";
import { Typography, TypographyProps } from "@mui/joy";
import { SxProps } from "@mui/joy/styles/types";

interface CustomTypographyHighlightProps extends TypographyProps {
  // 하이라이팅이 필요한 텍스트 (옵션) - 단일 문자열
  highlightedText?: string;
  // 하이라이팅이 필요한 텍스트 목록 (옵션) - 여러 개 지원
  highlightedTexts?: string[];
  // 하이라이트 부분에 적용할 variant (옵션)
  highlightVariant?: TypographyProps["variant"];
  // 하이라이트 부분에 적용할 sx (옵션)
  highlightSx?: SxProps;
}

export default function CustomTypographyHighlight({
  children,
  sx,
  highlightedText,
  highlightedTexts,
  highlightVariant,
  highlightSx,
  ...rest
}: CustomTypographyHighlightProps) {
  // 1) 만약 children이 문자열이 아닐 경우, 그대로 렌더링 처리
  if (typeof children !== "string") {
    return (
      <Typography
        sx={{
          p:0,
          whiteSpace: "pre-wrap",
          wordBreak: "keep-all",
          overflowWrap: "break-word",
          lineBreak: "strict",
          ...sx,
        }}
        {...rest}
      >
        {children}
      </Typography>
    );
  }

  // 정규식 특수문자 이스케이프 유틸
  const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // 2) children이 문자열인 경우, 하이라이트 단어(들)가 있으면 파싱
  let parsedChildren: React.ReactNode = children;

  const terms = [
    ...(Array.isArray(highlightedTexts) ? highlightedTexts : []),
    ...(highlightedText ? [highlightedText] : []),
  ].filter((t): t is string => typeof t === "string" && t.length > 0);

  if (terms.length > 0) {
    const pattern = `(${terms.map(escapeRegExp).join("|")})`;
    const regex = new RegExp(pattern, "g");
    const parts = children.split(regex);

    parsedChildren = parts.map((part, idx) => {
      if (terms.includes(part)) {
        // 하이라이트 부분
        return (
          <Typography
            key={idx}
            component="span"
            variant={highlightVariant}
            sx={{
              p: 0,
              m: 0,
              ...highlightSx,
            }}
          >
            {part}
          </Typography>
        );
      } else {
        // 일반 텍스트
        return <React.Fragment key={idx}>{part}</React.Fragment>;
      }
    });
  }

  return (
    <Typography
      sx={{
        whiteSpace: "pre-wrap",
        wordBreak: "keep-all",
        overflowWrap: "break-word",
        lineBreak: "strict",
        ...sx,
      }}
      {...rest}
    >
      {parsedChildren}
    </Typography>
  );
}